import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../endpoints';
import {
  Product,
  ProductListResponse,
  ProductFilters,
  DetailedProduct,
  CreateProductRequest,
  UpdateProductRequest,
  UpdateStatusRequest,
  CatalogueConfig,
  CatalogueConfigResponse,
} from '../types/productTypes';

/**
 * Get products list (main listing endpoint with filters)
 */
export const getProducts = async (filters: ProductFilters): Promise<ProductListResponse> => {
  try {
    const queryParams: any = {};
    if (filters.pageNum) queryParams.pageNum = filters.pageNum;
    if (filters.pageSize) queryParams.pageSize = filters.pageSize;
    if (filters.sport) queryParams.sport = filters.sport;
    if (filters.category) queryParams.category = filters.category;
    if (filters.status && filters.status !== 'All') {
      queryParams.status = filters.status;
    }
    if (filters.search) {
      queryParams.search = filters.search;
    }
    if (filters.sellerLocation) {
      queryParams.sellerLocation = filters.sellerLocation;
    }

    const response = await secureApi.get<any>(SHOP_ENDPOINTS.PRODUCTS.LIST, {
      params: queryParams,
    });

    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    // Helper function to extract first image from colorImages
    const getFirstImage = (product: any): string => {
      if (product.images && product.images.length > 0) {
        return product.images[0];
      }
      if (product.colorImages && typeof product.colorImages === 'object') {
        const colorKeys = Object.keys(product.colorImages);
        if (colorKeys.length > 0 && Array.isArray(product.colorImages[colorKeys[0]])) {
          const firstColorImages = product.colorImages[colorKeys[0]];
          if (firstColorImages.length > 0) {
            return firstColorImages[0];
          }
        }
      }
      return '';
    };
    
    // Helper function to calculate discount
    const calculateDiscount = (mrp: number, sellingPrice: number): number => {
      if (mrp && sellingPrice && mrp > sellingPrice) {
        return Math.round(((mrp - sellingPrice) / mrp) * 100);
      }
      return 0;
    };
    
    return {
      paginatedResults: (apiData.paginatedResults || apiData.products || []).map((product: any) => {
        const pricing = product.pricing || {};
        const sellingPrice = pricing.sellingPrice || 0;
        const mrp = pricing.mrp || sellingPrice;
        const stockQty = pricing.stockQty || 0;
        const discount = calculateDiscount(mrp, sellingPrice);
        
        return {
          ...product,
          _id: product._id,
          productId: product.productId || product._id,
          name: product.name || product.productName || '',
          quantity: stockQty,
          price: sellingPrice,
          mrp: mrp,
          discount: discount,
          status: product.status || (stockQty > 0 ? 'ACTIVE' : 'INACTIVE'),
          image: getFirstImage(product),
          category: product.category || product.productCategory,
          sport: product.sport || product.productSport,
          badges: product.badges || [],
          rating: product.rating || { avg: 0, count: 0 },
          colorImages: product.colorImages || {},
        };
      }),
      totalCount: apiData.totalCount || 0,
      totalPages: apiData.totalPages || Math.ceil((apiData.totalCount || 0) / (filters.pageSize || 10)),
      page: filters.pageNum || 1,
      activeCount: apiData.activeCount || 0,
      inactiveCount: apiData.inactiveCount || 0,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get products by filters (supports sellerLocation filter)
 */
export const getProductsByFilters = async (filters: ProductFilters): Promise<ProductListResponse> => {
  try {
    const queryParams: any = {};
    if (filters.pageNum) queryParams.pageNum = filters.pageNum;
    if (filters.pageSize) queryParams.pageSize = filters.pageSize;
    if (filters.sellerLocation) queryParams.sellerLocation = filters.sellerLocation;
    if (filters.status && filters.status !== 'All') {
      queryParams.status = filters.status;
    }
    if (filters.search) {
      queryParams.search = filters.search;
    }

    const response = await secureApi.get<any>(SHOP_ENDPOINTS.PRODUCTS.LIST_BY_FILTERS, {
      params: queryParams,
    });

    // Handle API response structure
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      paginatedResults: (apiData.paginatedResults || apiData.products || []).map((product: any) => ({
        ...product,
        _id: product._id,
        productId: product.productId || product._id,
        name: product.name || product.productName || '',
        quantity: product.quantity || product.qty || 0,
        price: product.price || 0,
        status: product.status || 'INACTIVE',
      })),
      totalCount: apiData.totalCount || 0,
      totalPages: apiData.totalPages || Math.ceil((apiData.totalCount || 0) / (filters.pageSize || 10)),
      page: filters.pageNum || 1,
      activeCount: apiData.activeCount || 0,
      inactiveCount: apiData.inactiveCount || 0,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get product by ID (detailed)
 */
export const getProductById = async (id: string): Promise<DetailedProduct> => {
  try {
    const response = await secureApi.get<any>(SHOP_ENDPOINTS.PRODUCTS.GET_BY_ID(id));
    
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return {
      ...apiData,
      _id: apiData._id,
      seller: apiData.seller || '',
      sport: apiData.sport || '',
      category: apiData.category || '',
      gender: apiData.gender || 'UNISEX',
      name: apiData.name || '',
      description: apiData.description || '',
      pricing: apiData.pricing || { mrp: 0, sellingPrice: 0, stockQty: 0 },
      badges: apiData.badges || [],
      colorImages: apiData.colorImages || {},
      images: apiData.images || [],
      variants: apiData.variants || [],
      fulfillmentCapabilities: apiData.fulfillmentCapabilities || ['PICKUP'],
      status: apiData.status || 'INACTIVE',
      additionalInfo: apiData.additionalInfo || { productDesc: '', material: '', misc: '' },
      sellerLocations: apiData.sellerLocations || [],
      estimatedPickupDays: apiData.estimatedPickupDays || 5,
      visibility: apiData.visibility || { isFeatured: false, featuredRank: 1 },
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Create new product
 */
export const createProduct = async (productData: CreateProductRequest): Promise<DetailedProduct> => {
  try {
    // Clean and format the payload to match API expectations exactly
    const cleanedData = { ...productData };
    
    // Remove discount and stockQty from pricing if they exist (they're not allowed in API)
    if (cleanedData.pricing) {
      const { discount, stockQty, ...pricingCleaned } = cleanedData.pricing as any;
      cleanedData.pricing = pricingCleaned;
    }
    
    // Ensure required fields are properly formatted
    const apiPayload: any = {
      seller: cleanedData.seller || '',
      ...(cleanedData.sport ? { sport: cleanedData.sport } : {}),
      category: cleanedData.category || '',
      gender: cleanedData.gender || 'UNISEX',
      name: cleanedData.name || '',
      description: cleanedData.description || '',
      pricing: {
        mrp: cleanedData.pricing?.mrp || 0,
        sellingPrice: cleanedData.pricing?.sellingPrice || 0
      },
      badges: cleanedData.badges || [],
      colorImages: cleanedData.colorImages || {},
      images: cleanedData.images || [],
      variants: cleanedData.variants || [],
      fulfillmentCapabilities: cleanedData.fulfillmentCapabilities || ['PICKUP'],
      status: cleanedData.status || 'ACTIVE',
      additionalInfo: {
        productDesc: cleanedData.additionalInfo?.productDesc || '',
        material: cleanedData.additionalInfo?.material || '',
        misc: cleanedData.additionalInfo?.misc || ''
      },
      sellerLocations: cleanedData.sellerLocations || [],
      adsId: cleanedData.adsId || '',
      estimatedPickupDays: cleanedData.estimatedPickupDays || 5,
      visibility: {
        isFeatured: cleanedData.visibility?.isFeatured || false,
        featuredRank: cleanedData.visibility?.featuredRank || 1
      }
    };

    const response = await secureApi.post<any>(SHOP_ENDPOINTS.PRODUCTS.ADD, apiPayload);
    
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return apiData;
  } catch (error) {
    throw error;
  }
};

/**
 * Update existing product
 */
export const updateProduct = async (id: string, productData: UpdateProductRequest): Promise<DetailedProduct> => {
  try {
    // Clean and format the payload to match API expectations exactly
    const cleanedData = { ...productData };
    
    // Remove discount and stockQty from pricing if they exist (they're not allowed in API)
    if (cleanedData.pricing) {
      const { discount, stockQty, ...pricingCleaned } = cleanedData.pricing as any;
      cleanedData.pricing = pricingCleaned;
    }
    
    // Ensure required fields are properly formatted (same as create)
    const apiPayload: any = {
      seller: cleanedData.seller || '',
      ...(cleanedData.sport ? { sport: cleanedData.sport } : {}),
      category: cleanedData.category || '',
      gender: cleanedData.gender || 'UNISEX',
      name: cleanedData.name || '',
      description: cleanedData.description || '',
      pricing: {
        mrp: cleanedData.pricing?.mrp || 0,
        sellingPrice: cleanedData.pricing?.sellingPrice || 0
      },
      badges: cleanedData.badges || [],
      colorImages: cleanedData.colorImages || {},
      images: cleanedData.images || [],
      variants: cleanedData.variants || [],
      fulfillmentCapabilities: cleanedData.fulfillmentCapabilities || ['PICKUP'],
      status: cleanedData.status || 'ACTIVE',
      additionalInfo: {
        productDesc: cleanedData.additionalInfo?.productDesc || '',
        material: cleanedData.additionalInfo?.material || '',
        misc: cleanedData.additionalInfo?.misc || ''
      },
      sellerLocations: cleanedData.sellerLocations || [],
      adsId: cleanedData.adsId || '',
      estimatedPickupDays: cleanedData.estimatedPickupDays || 5,
      visibility: {
        isFeatured: cleanedData.visibility?.isFeatured || false,
        featuredRank: cleanedData.visibility?.featuredRank || 1
      }
    };

    const response = await secureApi.post<any>(SHOP_ENDPOINTS.PRODUCTS.UPDATE(id), apiPayload);
    
    const responseData = (response as any)?.data || response;
    const apiData = responseData?.data || responseData;
    
    return apiData;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete product
 */
export const deleteProduct = async (id: string): Promise<void> => {
  try {
    await secureApi.post<any>(SHOP_ENDPOINTS.PRODUCTS.DELETE(id));
  } catch (error) {
    throw error;
  }
};

/**
 * Update product status (for toggle in listing)
 */
export const updateProductStatus = async (id: string, status: 'ACTIVE' | 'INACTIVE'): Promise<void> => {
  try {
    const statusData: UpdateStatusRequest = { status };
    await secureApi.post<any>(SHOP_ENDPOINTS.PRODUCTS.UPDATE_STATUS(id), statusData);
  } catch (error) {
    throw error;
  }
};

/**
 * Get catalogue configuration data for dropdowns and filters
 */
export const getCatalogueConfig = async (): Promise<CatalogueConfig> => {
  try {
    const response = await secureApi.get<CatalogueConfigResponse>(SHOP_ENDPOINTS.PRODUCTS.CONFIGS);
    
    const responseData = (response as any)?.data || response;
    
    // Handle different response structures
    if (responseData.data) {
      return responseData.data;
    }
    
    return responseData;
  } catch (error) {
    throw error;
  }
};
